import { Injectable } from '@angular/core';
import {CommonService} from './common.service';
import {AngularFirestore} from '@angular/fire/firestore';
import {SessionService} from './session.service';
import {ALERT_TEMPLATE, COLLECTION_NAME, DOCUMENT_NAME, KEISHA_SHUBETSU, LAYOUT_TEMPLATE} from '../../shared/constants';
import firebase from 'firebase';
import {Keisha, KeishaConfig} from "../../shared/interfaces/keisha";
import {MessageService} from "./message.service";
import {PushText} from "./firestore-master.service";
import {MasterService} from './master.service';
import {MokuhyouTemperature, MokuhyouTemperatureRow} from '../../shared/interfaces/mokuhyou-tempreture';
import {AngularFireFunctions} from '@angular/fire/functions';
import cloneDeep from "lodash-es/cloneDeep";
import {KeishaConfigMapValue} from "../../masters/keishas/keishas.component";
import {WithNullable} from "../../shared/typeAliases";
import {setOperatorList} from './keisha-hibetsu-file.service';
import {firstValueFrom} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class KeishaService {
  defaultPushText: PushText = { info: '追加しました', err: '追加に失敗しました' };
  constructor(
    private db: AngularFirestore,
    private sessionService: SessionService,
    private commonService: CommonService,
    private messageService: MessageService,
    private afn: AngularFireFunctions,
    private masterService: MasterService
  ) { }

  /**
   * collectionName へのリファレンスを返す
   * @param collectionName
   */
  genCustomerRootCollectionRef(collectionName: string) : firebase.firestore.CollectionReference<firebase.firestore.DocumentData> {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId).collection(collectionName);
  }

  /**
   * 鶏舎configのrefを返す
   * @param keishaId
   */
  genKeishaConfigRef(keishaId: string) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.KEISHAMASTER).doc(keishaId)
      .collection(COLLECTION_NAME.KEISHACONFIG).doc(DOCUMENT_NAME.KEISHACONFIGDOC);
  }

  genKeishaConfigTrueRef(keishaId: string) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.KEISHAMASTER).doc(keishaId)
      .collection(COLLECTION_NAME.KEISHACONFIG).doc(DOCUMENT_NAME.KEISHACONFIGDOC);
  }

  /**
   * 鶏舎configのobservableを返す
   * @param keishaId
   */
  getKeishaConfigObservabele(keishaId: string) {
    return this.genKeishaConfigRef(keishaId).valueChanges();
  }

  /**
   * 鶏舎configのpromiseを返す
   * @param keishaId
   */
  getKeishaConfig(keishaId: string) {
    return firstValueFrom(this.genKeishaConfigRef(keishaId).get());
  }

  /**
   * 鶏舎config 保存処理
   * @param keishaMasterFormDirty 変更があれば true なければ false
   * @param keishaConfigFormDirty 変更があれば true なければ false
   * @param keishaId
   * @param keishaConfigMap Map<keishaId, { edit: keishaConfig, origin: keishaConfig }>
   * @param create 新規登録時はtrue, 更新時はfalse
   */
  saveKeishaConfig(keishaMasterFormDirty: boolean, keishaConfigFormDirty: boolean, keishaId: string, keishaConfigMap: Map<string, KeishaConfigMapValue>, create= false) {
    const keishaConfigMapId = create ? '' : keishaId;
    // 新規追加鶏舎config取得
    const keishaConfig = keishaConfigMap.get(keishaConfigMapId);
    // 保存した鶏舎マスターのidを新規追加鶏舎configのidにセット
    keishaConfigMap.set(keishaId, keishaConfig ? keishaConfig : { edit: this.getInitialKeishaConfig(), origin: this.getInitialKeishaConfig() });
    // 鶏舎マスタが登録される場合はメッセージ出さない。
    // 新規追加、鶏舎マスタに変更がある場合はマスタが更新されてテキストが表示されるので、表示されないように pushtext セット
    const pushText = create || (keishaMasterFormDirty && !keishaConfigFormDirty)
      ? { info: '', err: '' } : { info: '登録しました', err: '登録に失敗しました' };
    // toDelete: true の行を削除
    // TODO: 目標温度設定の行が1行もない場合、空配列
    if (keishaConfig) {
      keishaConfig.edit.currentMokuhyouTemperatureRows = this.commonService.removeElemToDelete(keishaConfig?.edit.currentMokuhyouTemperatureRows);
      // 不要なプロパティ削除
      this.preSaveKeishaConfig(keishaConfig?.edit);
    }
    // 鶏舎configを保存
    this.saveKeishaConfigDoc(keishaId, keishaConfig ? keishaConfig.edit : this.getInitialKeishaConfig(), pushText);
    // 新規追加用の鶏舎configを初期化
    keishaConfigMap.set('', { edit: this.getInitialKeishaConfig(), origin: this.getInitialKeishaConfig() });
  }

  /**
   * 鶏舎config document 保存処理
   * @param keishaId
   * @param keishaConfig
   * @param pushText
   */
  saveKeishaConfigDoc(keishaId: string, keishaConfig: KeishaConfig, pushText: PushText = this.defaultPushText) {
    const keishaConfigRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.KEISHAMASTER).doc(keishaId)
      .collection(COLLECTION_NAME.KEISHACONFIG).doc(DOCUMENT_NAME.KEISHACONFIGDOC);
    // ここでbatch処理は特に意図はない
    const batch = this.db.firestore.batch();
    batch.set(keishaConfigRef, keishaConfig, {merge: true});
    batch.commit()
      .then(() => {
        if (pushText?.info) {
          this.messageService.pushInfo(pushText.info);
        }
        console.log('KeishaConfig document successfully registered!');
      })
      .catch((e) => {
        if (pushText?.err) {
          this.messageService.pushError(pushText.err);
        }
        console.error(e);
      });
  }

  /**
   * 鶏舎マスタのoperatorsを当日の鶏舎日別ファイルに反映させる
   * operatorsが増えていない場合は書き込みを行わない
   * @param keisha
   * @param pushText
   */
  async updateKeishaHibetsuFile(keisha: Keisha, pushText: PushText = this.defaultPushText) {
    const keishaHibetsuFileRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.KEISHAMASTER).doc(keisha.id)
      .collection(COLLECTION_NAME.KEISHAHIBETSUFILE).doc(this.commonService.getDateWithString());
    const snapshot = await keishaHibetsuFileRef.get();
    const keishaHibetsuFile = this.commonService.updateKeishaHibetsuFileV1ToV2(snapshot.data());
    if (!keishaHibetsuFile) { return; }
    const beforeUpdated = cloneDeep(keishaHibetsuFile.operatorList ?? []);
    setOperatorList(keishaHibetsuFile, keisha.operators);
    // 変更が無い場合は書き込まない。
    // setOperatorList()の動作的にoperatorList.lengthが短くなることは想定しない。
    if (beforeUpdated.length >= keishaHibetsuFile.operatorList.length) { return; }
    keishaHibetsuFileRef.set(keishaHibetsuFile, {merge: true})
      .then(() => {
        if (pushText?.info) {
          this.messageService.pushInfo(pushText.info);
        }
        console.log('KeishaHibetsuFile document is successfully updated!');
      })
      .catch((e) => {
        if (pushText?.err) {
          this.messageService.pushError(pushText.err);
        }
        console.error(e);
      });
  }

  /**
   * 不要なプロパティを削除
   * 鶏舎config: 目標温度設定行、アラートプログラム
   * @param keishaConfig
   */
  preSaveKeishaConfig(keishaConfig: KeishaConfig) {
    this.commonService.deletePropertiesFromObjectArray2(keishaConfig.currentMokuhyouTemperatureRows, ['hover', 'toDelete']);
    this.commonService.deletePropertiesFromObjectArray2(keishaConfig.alertProgram, ['hover']);
  }

  /**
   * 鶏舎configの初期値を返す
   */
  getInitialKeishaConfig(): KeishaConfig {
    return {
      latestEdukeDate: '',
      shiyoulotUpdatedFromMobileDate: '',
      alertProgram: ALERT_TEMPLATE,
      currentMokuhyouTemperatureId: 'free',
      currentMokuhyouTemperatureRows: [this.getInitialMokuhyouTemperatureRow()],
      originalMokuhyouTemperatureMaster: [],
      autoSetMokuhyouTemperature: true,
      isValidMokuhyouTemperature: true,
      updateId: ''
    };
  }

  /**
   * 目標温度設定の初期値を返す
   */
  getInitialMokuhyouTemperatureRow(): MokuhyouTemperatureRow {
    return {
      startNichirei: 1,
      lowTemperature: 10,
      highTemperature: 30
    }
  }

  /**
   * 鶏舎マスタ初期値を返す
   */
  genInitialKeisha(): WithNullable<Keisha, 'farmId' | 'keishaShubetsu' | 'tentouProgramId'> {
    return {
      id: '',
      name: '',
      farmId: null,
      farmName: '',
      keishaShubetsu: null,
      feedTankQuantity: 1,
      feedTankCapacity: 10000,
      feedTankCapacity_t: 10,
      inputKyujiRyou: true, // 給餌量をkg単位で直接入力する場合、true
      esaHansouRyouPerMinute: 0, // 搬送量を搬送の稼働時間で入力する際、1分あたり何kg供給されるか
      calcEsaZan: false,
      tentouProgramId: null,
      layout: LAYOUT_TEMPLATE,
      operators: [], // モバイル端末操作者 {name: string, toDelete?: boolean}
      alertSoushinSakiAddress: [], // アラート送信先アドレス {mail: string}
      isDeleted: false
    };
  }

  /**
   * 鶏舎configのcurrentMokuhyouTemperatureRowsをセット
   * 鶏舎固有の目標温度設定があれば、それを currentMokuhyouTemperatureRows にセット
   * 無ければマスターの目標温度設定の配列を鶏舎configにセット
   * @param keishaConfig
   * @param mokuhyouTemperatureList 目標温度設定List(master + default設定(カスタマイズ))
   */
  setMokuhyouTemperature(keishaConfig: KeishaConfig, mokuhyouTemperatureList: MokuhyouTemperature[]) {
    // 鶏舎固有の目標温度設定があれば、それを currentMokuhyouTemperatureRows にセットする
    if (keishaConfig.originalMokuhyouTemperatureMaster && keishaConfig.originalMokuhyouTemperatureMaster.length > 0) {
      // 鶏舎固有の目標温度設定
      const tempOriginalMokuhyouTemperature = keishaConfig.originalMokuhyouTemperatureMaster.find(mt => {
        return mt.id === keishaConfig.currentMokuhyouTemperatureId;
      });
      // 鶏舎固有の目標温度設定があれば、鶏舎固有の目標温度設定の配列を鶏舎configにセット
      // 無ければマスターの目標温度設定の配列を鶏舎configにセット
      if (tempOriginalMokuhyouTemperature) {
        keishaConfig.currentMokuhyouTemperatureRows = tempOriginalMokuhyouTemperature.mokuhyouTemperatureRows;
      } else {
        keishaConfig.currentMokuhyouTemperatureRows = this.getMasterMokuhyouTemperatureRows(keishaConfig.currentMokuhyouTemperatureId, mokuhyouTemperatureList);
      }
    } else {
      // マスターの目標温度設定の配列を鶏舎configにセット
      keishaConfig.currentMokuhyouTemperatureRows = this.getMasterMokuhyouTemperatureRows(keishaConfig.currentMokuhyouTemperatureId, mokuhyouTemperatureList);
    }
  }

  /**
   * 目標温度設定List(master + default設定(カスタマイズ))から mokuhyouTemperatureRows を取得
   * 指定されたidがない場合、デフォルト設定(開始日齢:1 最低温度: 10, 最高温度: 30) の配列を返す
   * @param mokuhyouTemperatureId
   * @param mokuhyouTemperatureList 目標温度設定List(master + default設定(カスタマイズ))
   */
  getMasterMokuhyouTemperatureRows(mokuhyouTemperatureId: string, mokuhyouTemperatureList: MokuhyouTemperature[]) {
    const tempMokuhyouTemperature = mokuhyouTemperatureList.find(mt => {
      return mt.id === mokuhyouTemperatureId;
    });
    return tempMokuhyouTemperature
      ? tempMokuhyouTemperature.mokuhyouTemperatureRows : [this.getInitialMokuhyouTemperatureRow()];
  }

  /**
   * 鶏舎固有の目標温度設定を追加
   * TODO 鶏舎固有の目標温度設定を画面に表示させて編集可能にする？
   * @param keishaConfig
   */
  setOriginalMokuhyouTemperature(keishaConfig: KeishaConfig) {
    const tempMokuhyouTemperature = keishaConfig.originalMokuhyouTemperatureMaster.find(mtm =>
      mtm.id === keishaConfig.currentMokuhyouTemperatureId
    );
    const tempCurrentMokuhyouTemperatureRows = cloneDeep(keishaConfig.currentMokuhyouTemperatureRows);
    const filteredTempCurrentMokuhyouTemperatureRows = tempCurrentMokuhyouTemperatureRows.filter(mtr => !mtr.toDelete);
    if (tempMokuhyouTemperature) {
      tempMokuhyouTemperature.mokuhyouTemperatureRows = filteredTempCurrentMokuhyouTemperatureRows;
    } else {
      keishaConfig.originalMokuhyouTemperatureMaster.push(
        { id: keishaConfig.currentMokuhyouTemperatureId,
          mokuhyouTemperatureRows: filteredTempCurrentMokuhyouTemperatureRows
        });
    }
  }

  /**
   * KeishaConfig型と比較して、不足しているプロパティを追加し、keishaConfigを返す
   * 第二期開発KeishaConfig型への追加分を補う
   * @param keishaConfig
   */
  fillKeishaConfigProp(keishaConfig: KeishaConfig | undefined) {
    keishaConfig = keishaConfig ? keishaConfig : this.getInitialKeishaConfig();
    keishaConfig.alertProgram = keishaConfig.alertProgram ? keishaConfig.alertProgram : ALERT_TEMPLATE;
    keishaConfig.currentMokuhyouTemperatureId = keishaConfig.currentMokuhyouTemperatureId ? keishaConfig.currentMokuhyouTemperatureId : 'free';
    keishaConfig.currentMokuhyouTemperatureRows = keishaConfig.currentMokuhyouTemperatureRows ? keishaConfig.currentMokuhyouTemperatureRows : [this.getInitialMokuhyouTemperatureRow()];
    keishaConfig.originalMokuhyouTemperatureMaster = keishaConfig.originalMokuhyouTemperatureMaster ? keishaConfig.originalMokuhyouTemperatureMaster : [];
    keishaConfig.latestEdukeDate = keishaConfig.latestEdukeDate ? keishaConfig.latestEdukeDate : '';
    keishaConfig.autoSetMokuhyouTemperature = typeof keishaConfig.autoSetMokuhyouTemperature !== 'undefined'
      ? keishaConfig.autoSetMokuhyouTemperature : true;
    return keishaConfig;
  }
  //# region データ修正用処理 アプリでは使わない
  /**
   * データ移行用処理
   * 鶏舎Configを問答無用で一括更新
   * デフォルト値をセットする
   * 2022-08-10実行
   */
  setKeishaConfigDefaultValue() {
    console.log('setKeishaConfigDefaultValue start.');
    if (!confirm('更新しますか?')) {
      return;
    }
    const batch = this.db.firestore.batch();
    this.masterService.keishaMaster.forEach(keisha => {
      const keishaRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.KEISHAMASTER).doc(keisha.id);
      const feedTankCapacity = keisha.feedTankCapacity_t ? keisha.feedTankCapacity_t * 1000 : 10000;
      const feedTankQuantity = keisha.feedTankQuantity ? keisha.feedTankQuantity : 1;
      batch.set(keishaRef, {calcEsaZan: false, feedTankCapacity, feedTankQuantity}, {merge: true});

      const keishaConfigRef = this.genKeishaConfigTrueRef(keisha.id);
      const obj: any = this.getInitialKeishaConfig();
      delete obj.latestEdukeDate;
      // 育成の場合はfalse
      if (keisha.keishaShubetsu === KEISHA_SHUBETSU.IKUSEI) {
        obj.autoSetMokuhyouTemperature = false;
      }
      batch.set(keishaConfigRef, obj, {merge: true});
    });
    batch.commit();
  }

  /**
   * データ移行用処理
   * 餌残をt→kg単位に移行するために
   * 全ての値を1000倍する
   * 2022-08-10実行
   */
  async setEsaZanDefaultValue() {
    console.log('setEsaZanDefaultValue start.');
    if (!confirm('更新しますか?')) {
      return
    }
    const batch = this.db.firestore.batch();
    for (const keisha of this.masterService.keishaMaster) {
      const snapshot = await this.genCustomerRootCollectionRef(COLLECTION_NAME.KEISHAMASTER).doc(keisha.id)
        .collection(COLLECTION_NAME.KEISHAHIBETSUFILE).doc(this.commonService.getDateWithString()).get();
      if (!snapshot.exists) { continue; }
      const keishaHibetsuFile = this.commonService.updateKeishaHibetsuFileV1ToV2(snapshot.data());
      Object.keys(keishaHibetsuFile.esaZan).forEach(esaZanKey => {
        keishaHibetsuFile.esaZan[esaZanKey] = keishaHibetsuFile.esaZan[esaZanKey] ? keishaHibetsuFile.esaZan[esaZanKey] * 1000 : 0;
        if (isNaN(keishaHibetsuFile.esaZan[esaZanKey])) {
          keishaHibetsuFile.esaZan[esaZanKey] = 0;
        }
      })
      console.log(keishaHibetsuFile.id);
      console.log(keishaHibetsuFile.keishaId);
      console.table(keishaHibetsuFile.esaZan);
      batch.set(snapshot.ref, keishaHibetsuFile, {merge: true});
    }
    if (!confirm('更新しますか?')) {
      return
    }
    batch.commit().then(() => console.log('setEsaZanDefaultValue end.'));
  }

  /**
   * 現在羽数更新処理を手動で実行
   * WARNING: 実行当日において既に羽数の変動が登録されている場合、
   *          実行すべきではないかも
   * @param keishaId
   */
  toriggerUpdateGenzaiWasuu(keishaId: string) {
    console.log('toriggerUpdateGenzaiWasuu start.');
    if (!confirm('更新しますか?')) {
      return
    }
    const customerId = this.sessionService.session.personalSetting.customerId;
    firstValueFrom(this.afn.httpsCallable('\thibetsuFile-hibetsuFileFunctions-updateGenzaiWasuuManualTrigger')({customerId, keishaId}))
      .then(res => console.log(res))
      .catch(res => console.log(res));
  }

  //# endregion
}
